import { v4 as uuidv4 } from "uuid";
import {
  WebSocketClient,
  WebSocketDefaultEvents,
  WebSocketEvent,
} from "@plutoxyz/frame-js";
import plutoSdk from "../plutoSdk";
import { useAuthStore } from "../store/authStore";
import { SocketActionTypes } from "../types/enums";
import {
  handleClose,
  handleDone,
  handleError,
  handleFinished,
  handleMetadata,
  handleScriptLog,
  handleRequestOauth,
  handleRequestPrompt,
  handleTimeout,
  handleUserNetwork,
  handleUserNetworkDone,
  handleCdpTarget,
} from "./handlers";
import { fetchWithErrorHandling } from "../utils/utils";
import { useDemoStore } from "../store/demoStore";

let webSocketClient: WebSocketClient;
class Client {
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private baseDelay: number = 100;
  private maxDelay: number = 10000;
  private wasSessionRestored: boolean = false;
  private sessionId: string;
  private stabilityTimer: NodeJS.Timeout | null = null;

  constructor() {}

  init() {
    webSocketClient = new WebSocketClient(
      `${useDemoStore.getState().endpoints.socket}/connect`
    );

    const previousSessionId = sessionStorage.getItem("pluto-session-id");

    if (previousSessionId) {
      console.debug("Using existing session ID:", previousSessionId);
      this.wasSessionRestored = true;
      this.sessionId = previousSessionId;
    } else {
      this.sessionId = uuidv4();
    }
    sessionStorage.setItem("pluto-session-id", this.sessionId);
    useAuthStore.setState({ sessionId: this.sessionId });
    this.connect();

    webSocketClient.on(WebSocketEvent.MESSAGE, this.handleMessage);
    webSocketClient.on(WebSocketEvent.OPEN, this.handleOpen);
    webSocketClient.on(WebSocketEvent.CLOSE, this.handleClose);
    webSocketClient.on(WebSocketEvent.ERROR, this.handleError);

    const data = sessionStorage.getItem(`${this.sessionId}-screen`);
    if (data) {
      const { prompts, oauth } = JSON.parse(data);
      if (prompts) {
        handleRequestPrompt(prompts, this.sessionId);
      } else if (oauth) {
        handleRequestOauth(oauth);
      }
    }

    window.addEventListener("unload", () => {
      this.disconnect();
    });
  }

  connect() {
    webSocketClient.connect(this.sessionId);
  }

  disconnect() {
    webSocketClient.disconnect();
  }

  getWasSessionRestored() {
    return this.wasSessionRestored;
  }

  on(
    event: WebSocketEvent,
    callback: (event: WebSocketDefaultEvents[WebSocketEvent]) => void
  ) {
    webSocketClient.on(event, callback);
  }

  handleMessage = (payload: MessageEvent) => {
    const _data = JSON.parse(payload.data);
    if (!_data || !_data.payload || !_data.payload.type) return;

    const { data, type } = _data.payload;

    switch (type as SocketActionTypes) {
      case SocketActionTypes.REQUEST_PROMPT:
        handleRequestPrompt(data);
        break;
      case SocketActionTypes.SCRIPT_LOG:
        handleScriptLog(data);
        break;
      case SocketActionTypes.REQUEST_OAUTH:
        handleRequestOauth(data);
        break;
      case SocketActionTypes.DONE:
        handleDone(data);
        break;
      case SocketActionTypes.CLOSE:
        handleClose();
        break;
      case SocketActionTypes.ERROR:
        handleError(data);
        break;
      case SocketActionTypes.FINISHED:
        handleFinished(data);
        this.disconnect();
        break;
      case SocketActionTypes.TIMEOUT:
        handleTimeout();
        break;
      case SocketActionTypes.METADATA:
        handleMetadata(data);
        break;
      case SocketActionTypes.REQUEST_USER_NETWORK:
        handleUserNetwork(data);
        break;
      case SocketActionTypes.REQUEST_USER_NETWORK_DONE:
        handleUserNetworkDone();
        break;
      case SocketActionTypes.CDP_TARGET:
        handleCdpTarget(data);
        break;
      default:
        console.warn("Received message of unknown type", type);
        return;
    }

    if (_data.id) {
      webSocketClient.ack(_data.id);
    }
  };

  private async waitForFingerprint(
    maxRetries = 50,
    retryDelay = 100
  ): Promise<string> {
    for (let i = 0; i < maxRetries; i++) {
      const fingerprint = plutoSdk.getFingerprint();
      if (fingerprint !== null) {
        return fingerprint;
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
    }
    return "";
  }

  handleOpen = async () => {
    /**
     * We only want to call /metadata and /run_script
     * on our initial conncetion. If reconnectAttemps > 0,
     * it means we have reconnected to a broken connection,
     * and therefor don't need to run these again.
     */
    if (this.reconnectAttempts > 0) {
      this.stabilityTimer = setTimeout(() => {
        console.debug(
          "WebSocket connection stable for 30 seconds, resetting reconnect attempts"
        );
        this.reconnectAttempts = 0;
      }, 30000);

      console.debug("Websocket connection reestablished");
      return;
    }

    console.debug(`WebSocket connection established`);
    setTimeout(async () => {
      if (!this.getWasSessionRestored()) {
        const fingerprint = await this.waitForFingerprint();

        await fetchWithErrorHandling(
          `${useDemoStore.getState().endpoints.notary}/metadata`,
          {
            method: "POST",
            body: JSON.stringify({
              session_id: useAuthStore.getState().sessionId,
              origin: plutoSdk.hostOrigin,
              fp: fingerprint,
            }),
            errorCode: "metadata_error",
            displayMessage: "Failed to fetch metadata",
            responseType: "json",
          }
        );

        await fetchWithErrorHandling(
          `${useDemoStore.getState().endpoints.notary}/run_script`,
          {
            method: "POST",
            body: JSON.stringify({
              session_id: useAuthStore.getState().sessionId,
              script: btoa(
                unescape(encodeURIComponent(useDemoStore.getState().script))
              ),
            }),
            errorCode: "script_error",
            displayMessage: "Failed to execute script",
            responseType: "none",
          }
        );
      }
    }, 250);
  };

  handleClose = (event: CloseEvent) => {
    if (this.stabilityTimer) {
      clearTimeout(this.stabilityTimer);
      this.stabilityTimer = null;
    }

    if (!event.wasClean) {
      this.reconnectAttempts++;

      if (this.reconnectAttempts > this.maxReconnectAttempts) {
        console.warn(
          `WebSocket reconnection failed after ${this.maxReconnectAttempts} attempts. Giving up.`
        );
        useAuthStore.getState().deepClearSession();
        return;
      }

      const delay =
        this.reconnectAttempts === 1
          ? 0
          : Math.min(
              this.baseDelay * Math.pow(2, this.reconnectAttempts - 2),
              this.maxDelay
            );

      console.debug(
        `WebSocket connection closed. Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
      );

      setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.debug("WebSocket connection closed. Disconnecting");
      this.disconnect();
    }
  };

  /**
   * We don't specifically respond to handleError, since it is
   * handled in the handleClose function. Close events are given
   * better context about the error than error events.
   */
  handleError = (event: Event) => {};
}

export default new Client();
